"use client";

import useSWR, { SWRResponse } from "swr";
import useSWRImmutable from "swr/immutable";

import { IOrganization } from "@/backend/domains/organization/domain/Organization";

import { ServerAction as BaseServerAction, execAction } from "@/lib/executeServerAction";
import { useCurrentCabinetContext } from "@/stores/cabinet.store";
import { useOrganizationStore } from "@/stores/organization.store";
import { IKaspiCabinet } from "@/models/KaspiCabinet";
import { doIt } from "@/utils/doIt";
import { ActionData } from "@/utils/handleAction";

//

export type BaseServerActionKey = {
  key: string;
  organizationId?: string | null;
  cabinetId?: string;
} & Record<string, unknown>;

export type ServerActionKey = {
  key: string;
  organizationId?: string | boolean;
  cabinetId?: string | boolean;
} & Record<string, unknown>;

//

export interface ServerActionCallbackProps {
  organization: IOrganization;
  cabinet: IKaspiCabinet | null;
}

export type ServerAction<T> = (
  props: ServerActionCallbackProps,
) => Promise<ActionData<T> | undefined> | undefined;

//

export interface ServerActionOptions {
  immutable?: boolean;
  keepPreviousData?: boolean;
  disableToastOnError?: boolean;
}

//

export function useServerAction<T>(
  serverActionKey: ServerActionKey,
  action: (props: ServerActionCallbackProps) => Promise<ActionData<T>>,
  options?: ServerActionOptions,
): SWRResponse<T>;

export function useServerAction<T>(
  serverActionKey: ServerActionKey,
  action: (props: ServerActionCallbackProps) => Promise<ActionData<T> | undefined> | undefined,
  options?: ServerActionOptions,
): SWRResponse<T | undefined>;

export function useServerAction<T>(
  serverActionKey: ServerActionKey,
  action: ServerAction<T>,
  options?: ServerActionOptions,
) {
  const { key, cabinetId, organizationId, ...otherKey } = serverActionKey;

  const currentOrganization = useOrganizationStore();
  const currentCabinet = useCurrentCabinetContext((s) => s.cabinet ?? null);
  const currentCabinetId = currentCabinet?._id.toString();

  const cabinetIdKey = doIt(() => {
    if (typeof cabinetId === "string") return cabinetId;
    if (cabinetId === true) return currentCabinetId;
    return undefined;
  });

  const organizationIdKey = doIt(() => {
    if (typeof organizationId === "string") return organizationId;
    if (organizationId === true) return currentOrganization.id;
    return undefined;
  });

  return useBaseServerAction(
    { key, cabinetIdKey, organizationIdKey, ...otherKey },
    () => action({ cabinet: currentCabinet, organization: currentOrganization }),
    options,
  );
}

//

export function useBaseServerAction<T, Action extends BaseServerAction<T>>(
  serverActionKey: BaseServerActionKey,
  action: Action,
  options?: ServerActionOptions,
) {
  const { immutable, keepPreviousData, disableToastOnError } = options ?? {};

  return (immutable ? useSWRImmutable : useSWR)(
    createActionKey(serverActionKey),
    () => execAction(action, { enableToastOnError: disableToastOnError ? false : true }),
    { keepPreviousData },
  );
}

//

export function createActionKey(serverActionKey: BaseServerActionKey) {
  return JSON.stringify(serverActionKey);
}
